// Supabase Realtime is gone along with the rest of Supabase -- there is no
// drop-in replacement once writes go through self-hosted Postgres instead
// of Supabase's own managed instance (Realtime is Supabase's own layer on
// top of their logical replication, not a Postgres feature). This is a
// deliberate no-op until a replacement (e.g. Redis pub/sub + SSE from
// apps/api) is built, not a bug.
//
// Every affected query already has (or should have) its own
// refetchInterval/refetchOnWindowFocus fallback in lib/api.js -- see
// REALTIME_FALLBACK_POLL_MS there. The table -> query-key map this used to
// drive is kept here as a reference for whatever replaces it:
//
//   groups           -> ["groups"]
//   workshop_config   -> ["stakeholders"] (prefix match, per-location keys)
//   sentiment_history -> ["sentiment-history"]
//   message_log       -> ["message-log", "all"]
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { authBackend } from "./authBackend";

const EVENTS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/events`;
const DEBOUNCE_MS = 300;
const RETRY_MS = 3000;
const EXPIRY_SKEW_SEC = 30;

// table -> [query-key prefix, perGroup]
const TABLE_TO_KEY = {
    groups: [["groups"], false],
    message_log: [["message-log"], true],
    group_events: [["group-events"], true],
    sentiment_history: [["sentiment-history"], true],
    staff_tone_flags: [["staff-tone-flags"], true],
    complaints: [["complaints"], true],
    complaint_messages: [["complaints"], true],
    complaint_stage_history: [["complaints"], true],
    complaint_candidates: [["complaints"], true],
    workshop_stakeholders: [["stakeholders"], false],
    workshop_config: [["stakeholders"], false],
    intake_records: [["intake", "open"], false],
    feedback: [["feedback"], false],
};

const ALL_LIVE_KEYS = [
    ["groups"], ["message-log"], ["group-events"], ["sentiment-history"],
    ["staff-tone-flags"], ["complaints"], ["stakeholders"], ["intake", "open"], ["feedback"],
];

async function getToken() {
    let { data } = await authBackend.getSession();
    let session = data?.session;
    const exp = session?.expires_at;
    if (session && typeof exp === "number" && exp <= Math.floor(Date.now() / 1000) + EXPIRY_SKEW_SEC) {
        const refreshed = await authBackend.refreshSession();
        session = refreshed.data?.session || session;
    }
    return session?.access_token || null;
}

export function useRealtimeSync() {
    const queryClient = useQueryClient();

    useEffect(() => {
        const ctrl = new AbortController();
        const pending = new Map();
        let timer = null;
        let connectedOnce = false;
        console.log("[realtime] hook STARTED", new Date().toLocaleTimeString());
        const onVis = () => console.log("[realtime] tab is now", document.visibilityState, new Date().toLocaleTimeString());
        document.addEventListener("visibilitychange", onVis);

        const flush = () => {
            timer = null;
            console.log("[realtime] refetching:", [...pending.values()]);
            for (const { prefix, gid } of pending.values()) {
                queryClient.invalidateQueries({
                    queryKey: prefix,
                    predicate: gid == null
                        ? undefined
                        : (q) => {
                            const parts = q.queryKey.map(String);
                            return parts.includes("all") || parts.includes(String(gid));
                        },
                });
            }
            pending.clear();
        };

        const onEvent = (raw) => {
            let ev;
            try { ev = JSON.parse(raw); } catch { return; }
            console.log("[realtime] EVENT received:", ev);
            const entry = TABLE_TO_KEY[ev.table];
            if (!entry) return;
            const [prefix, perGroup] = entry;
            const gid = perGroup && ev.group_row_id ? ev.group_row_id : null;
            pending.set(`${ev.table}:${gid}`, { prefix, gid });
            if (!timer) timer = setTimeout(flush, DEBOUNCE_MS);
        };

        (async () => {
            while (!ctrl.signal.aborted) {
                let token = null;
                try {
                    token = await getToken();
                    if (ctrl.signal.aborted) return;
                    if (!token) {
                        console.log("[realtime] no token yet, retrying...");
                        await new Promise((r) => setTimeout(r, RETRY_MS));
                        continue;
                    }
                    await fetchEventSource(EVENTS_URL, {
                        signal: ctrl.signal,
                        headers: { Authorization: `Bearer ${token}` },
                        // Stay connected in a background tab. Closing on hide meant
                        // every return to the tab reconnected, and a reconnect
                        // refetches every live query at once.
                        openWhenHidden: true,
                        async onopen(res) {
                            const ok = res.ok && (res.headers.get("content-type") || "").includes("text/event-stream");
                            if (!ok) throw new Error(`events open failed: ${res.status}`);
                            console.log("[realtime] CONNECTED", new Date().toLocaleTimeString(), connectedOnce ? "(RECONNECT, refetching everything)" : "(first connect)");
                            if (connectedOnce) {
                                ALL_LIVE_KEYS.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));
                            }
                            connectedOnce = true;
                        },
                        onmessage(ev) { if (ev.data) onEvent(ev.data); },
                        onclose() { throw new Error("events stream closed"); },
                        onerror(err) { throw err; },
                    });
                } catch (err) {
                    if (ctrl.signal.aborted) return;
                    console.log("[realtime] DISCONNECTED, retrying in 3s:", err);
                }
                await new Promise((r) => setTimeout(r, RETRY_MS));
            }
        })();

        return () => {
            console.log("[realtime] hook STOPPED", new Date().toLocaleTimeString());
            document.removeEventListener("visibilitychange", onVis);
            ctrl.abort();
            if (timer) clearTimeout(timer);
        };
    }, [queryClient]);
}
